import appManager from '@app/appManager';
import AppModule from '@app/lib/AppModule';
import AnnouncementsModule from '@modules/AnnouncementsModule';
import FeatureFlagModule from '@modules/FeatureFlagModule';
import { FeatureFlagKeys } from '@modules/FeatureFlagModule/featureFlagKeys';
import type { NetworkLatencyData } from '@modules/DiagnosticsModule';
import { SEQUENTIAL_NETWORK_FAILURE_OFFLINE_THRESHOLD } from './constants';
import {
  computeWeightedAverageLatencyMs,
  latencyAverageToStatus,
  trimLatencyWindow,
} from './lib/latencyWindow';
import { appStatusState } from './state';
import { strings } from './strings';
import type { NetworkLatencyStatus } from './types';

export const NETWORK_OFFLINE_ANNOUNCEMENT_KEY = 'NETWORK_OFFLINE';
export const NETWORK_CRITICAL_ANNOUNCEMENT_KEY = 'NETWORK_CRITICAL';

type NetworkStatusPayload = {
  timestamp: number;
  isOnline: boolean;
};

type NetworkRequestFailedPayload = {
  operationName: string;
  duration: number;
  timestamp: string;
  errorType: string;
};

function offlineBannerTitle(): string {
  return `${strings.networkOffline.title}. ${strings.networkOffline.message}`;
}

function criticalLatencyBannerTitle(): string {
  return `${strings.networkCriticalLatency.title}. ${strings.networkCriticalLatency.message}`;
}

function showCriticalLatencyAnnouncement(): void {
  AnnouncementsModule.showAnnouncement(
    {
      status: 'warning',
      title: criticalLatencyBannerTitle(),
    },
    NETWORK_CRITICAL_ANNOUNCEMENT_KEY,
  );
}

export class AppStatusModule extends AppModule {
  override events: string[] = [];

  override preload(): void {
    this.app.events.on('network.status.offline', this.handleOfflineEvent);
    this.app.events.on('network.status.online', this.handleOnlineEvent);
    this.app.events.on(
      'diagnostics.network.latency',
      this.handleNetworkLatency,
    );
    this.app.events.on('network.request.failed', this.handleRequestFailed);

    FeatureFlagModule.addFlagListener(
      FeatureFlagKeys.featureNetworkMonitoringEnabled,
      ({ hasValue, value }) => {
        if (!hasValue || !value) {
          this.clearNetworkMonitoringEffects();
        }
      },
    );
  }

  override load(): void {
    if (
      this.isNetworkMonitoringEnabled() &&
      typeof navigator !== 'undefined' &&
      !navigator.onLine
    ) {
      this.handleOfflineEvent({
        timestamp: Date.now(),
        isOnline: false,
      });
    }
  }

  private isNetworkMonitoringEnabled(): boolean {
    return FeatureFlagModule.isEnabled(
      FeatureFlagKeys.featureNetworkMonitoringEnabled,
    );
  }

  private shouldTreatAsOfflineForMessaging(): boolean {
    return (
      appStatusState.isOffline ||
      appStatusState.sequentialFailedRequests >
        SEQUENTIAL_NETWORK_FAILURE_OFFLINE_THRESHOLD
    );
  }

  private clearNetworkMonitoringEffects(): void {
    AnnouncementsModule.hideAnnouncement(NETWORK_OFFLINE_ANNOUNCEMENT_KEY);
    AnnouncementsModule.hideAnnouncement(NETWORK_CRITICAL_ANNOUNCEMENT_KEY);
    appStatusState.latencyHistory = [];
    appStatusState.currentStatus = 'good';
    appStatusState.averageLatencyMs = null;
    appStatusState.lastUpdatedMs = null;
    appStatusState.sequentialFailedRequests = 0;
    appStatusState.isOffline =
      typeof navigator !== 'undefined' ? !navigator.onLine : false;
  }

  /**
   * Shows the offline-style banner when the browser reports offline or when
   * too many GraphQL fetches failed in a row while the browser still says "online".
   */
  private syncOfflineAnnouncement(): void {
    if (!this.isNetworkMonitoringEnabled()) {
      return;
    }

    if (this.shouldTreatAsOfflineForMessaging()) {
      AnnouncementsModule.hideAnnouncement(NETWORK_CRITICAL_ANNOUNCEMENT_KEY);
      AnnouncementsModule.showAnnouncement(
        {
          status: 'critical',
          title: offlineBannerTitle(),
        },
        NETWORK_OFFLINE_ANNOUNCEMENT_KEY,
      );
    } else {
      AnnouncementsModule.hideAnnouncement(NETWORK_OFFLINE_ANNOUNCEMENT_KEY);
      this.ensureCriticalLatencyBannerVisibleIfNeeded();
    }
  }

  private ensureCriticalLatencyBannerVisibleIfNeeded(): void {
    if (!this.isNetworkMonitoringEnabled()) {
      return;
    }
    if (this.shouldTreatAsOfflineForMessaging()) {
      return;
    }
    if (appStatusState.currentStatus === 'critical') {
      showCriticalLatencyAnnouncement();
    }
  }

  private readonly handleOfflineEvent = (payload: NetworkStatusPayload) => {
    if (!this.isNetworkMonitoringEnabled()) {
      return;
    }

    appStatusState.isOffline = true;
    appStatusState.sequentialFailedRequests = 0;
    appStatusState.lastUpdatedMs = payload.timestamp;
    this.app.logger.info('network offline', payload);

    this.syncOfflineAnnouncement();
  };

  private readonly handleOnlineEvent = (payload: NetworkStatusPayload) => {
    if (!this.isNetworkMonitoringEnabled()) {
      return;
    }

    appStatusState.isOffline = false;
    appStatusState.lastUpdatedMs = payload.timestamp;
    this.app.logger.info('network online', payload);

    this.refreshDerivedLatencyStatus();
    this.syncOfflineAnnouncement();
  };

  private readonly handleRequestFailed = (
    payload: NetworkRequestFailedPayload,
  ) => {
    if (!this.isNetworkMonitoringEnabled()) {
      return;
    }

    appStatusState.sequentialFailedRequests += 1;
    appStatusState.lastUpdatedMs = Date.now();
    this.app.logger.info('network request failed', {
      ...payload,
      sequentialFailedRequests: appStatusState.sequentialFailedRequests,
    });

    this.syncOfflineAnnouncement();
  };

  private readonly handleNetworkLatency = (data: NetworkLatencyData) => {
    if (!this.isNetworkMonitoringEnabled()) {
      return;
    }

    appStatusState.sequentialFailedRequests = 0;

    const now = Date.now();
    appStatusState.latencyHistory = trimLatencyWindow(
      [
        ...appStatusState.latencyHistory,
        { latencyMs: data.networkLatencyMs, timestampMs: now },
      ],
      now,
    );
    appStatusState.lastUpdatedMs = now;
    const avg = computeWeightedAverageLatencyMs(
      appStatusState.latencyHistory,
      now,
    );
    appStatusState.averageLatencyMs = avg;
    const next = latencyAverageToStatus(avg);
    this.handleLatencyStatusChange(next);
    this.syncOfflineAnnouncement();
  };

  private refreshDerivedLatencyStatus(): void {
    const now = Date.now();
    appStatusState.latencyHistory = trimLatencyWindow(
      appStatusState.latencyHistory,
      now,
    );
    appStatusState.lastUpdatedMs = now;
    const avg = computeWeightedAverageLatencyMs(
      appStatusState.latencyHistory,
      now,
    );
    appStatusState.averageLatencyMs = avg;
    const next = latencyAverageToStatus(avg);
    this.handleLatencyStatusChange(next);
  }

  private handleLatencyStatusChange(next: NetworkLatencyStatus): void {
    const prev = appStatusState.currentStatus;
    if (next === prev) {
      return;
    }

    appStatusState.currentStatus = next;
    this.app.logger.info('network health latency status changed', {
      from: prev,
      to: next,
      averageLatencyMs: appStatusState.averageLatencyMs,
    });

    if (this.shouldTreatAsOfflineForMessaging()) {
      AnnouncementsModule.hideAnnouncement(NETWORK_CRITICAL_ANNOUNCEMENT_KEY);
      return;
    }

    if (next === 'critical') {
      showCriticalLatencyAnnouncement();
    } else if (prev === 'critical') {
      AnnouncementsModule.hideAnnouncement(NETWORK_CRITICAL_ANNOUNCEMENT_KEY);
    }
  }
}

export default new AppStatusModule(
  appManager.events,
  appManager.logger('AppStatus'),
);
